// frontend/src/services/api.ts
import axios from 'axios';
const API_BASE_URL = `${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/api/v1`;

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: { 'Content-Type': 'application/json' },
});

// ─────────────────────────────────────────────
// INTERCEPTORS
// ─────────────────────────────────────────────
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      if (window.location.pathname !== '/') window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

// ─────────────────────────────────────────────
// AUTH
// ─────────────────────────────────────────────
export const authAPI = {
  register: (userData: { name: string; username: string; email: string; password: string }) =>
    api.post('/auth/register', userData),

  login: (credentials: { email: string; password: string }) =>
    api.post('/auth/login', credentials),

  logout: () => api.post('/auth/logout'),

  getProfile: () => api.get('/auth/me'),

  updateProfile: (data: { name?: string; username?: string; bio?: string; avatar?: string }) =>
    api.put('/auth/update-profile', data),

  changePassword: (data: { currentPassword: string; newPassword: string }) =>
    api.put('/auth/change-password', data),
};

// ─────────────────────────────────────────────
// MOVIES
// ─────────────────────────────────────────────
export const moviesAPI = {
  // General listing with filters
  getMovies: (params?: {
    search?: string;
    season?: string;
    holiday?: string;
    mood?: string;
    trope?: string;
    minRating?: number;
    decade?: number;
    sortBy?: string;
    order?: string;
    page?: number;
    limit?: number;
  }) => api.get('/movies', { params }),

  // Single movie with reviews + similar
  getMovie: (id: number | string) => api.get(`/movies/${id}`),

  // Special collections
  getTrending: (limit?: number) => api.get('/movies/trending', { params: { limit } }),
  getRomcomOfTheDay: () => api.get('/movies/romcom-of-the-day'),
  searchMovies: (q: string, limit?: number) => api.get('/movies/search', { params: { q, limit } }),

  // Filtered collections
  getBySeason: (season: string, page?: number) =>
    api.get(`/movies/season/${season}`, { params: { page } }),

  getByHoliday: (holiday: string, page?: number) =>
    api.get(`/movies/holiday/${holiday}`, { params: { page } }),

  getByMood: (mood: string, page?: number) =>
    api.get(`/movies/mood/${mood}`, { params: { page } }),

  // User interactions
  toggleFavorite: (movieId: number) => api.post(`/movies/${movieId}/favorite`),
  toggleWatchlist: (movieId: number) => api.post(`/movies/${movieId}/watchlist`),
  toggleWatched: (movieId: number) => api.post(`/movies/${movieId}/watched`),
  logNotebook: (movieId: number, data: { watchedOn: string; note?: string }) =>
    api.post(`/movies/${movieId}/notebook`, data),
  deleteNotebookEntry: (entryId: string) => api.delete(`/movies/notebook/${entryId}`),

  // User collections
  getUserFavorites: () => api.get('/movies/user/favorites'),
  getUserWatchlist: () => api.get('/movies/user/watchlist'),
  getUserWatched: () => api.get('/movies/user/watched'),
  getUserNotebook: () => api.get('/movies/user/notebook'),
};

// ─────────────────────────────────────────────
// REVIEWS
// ─────────────────────────────────────────────
export const reviewsAPI = {
  getMovieReviews: (movieId: number) => api.get(`/reviews/movies/${movieId}/reviews`),
  createReview: (movieId: number, data: { rating: number; content: string; containsSpoiler?: boolean }) =>
    api.post(`/reviews/movies/${movieId}/reviews`, data),
  updateReview: (reviewId: string, data: { rating?: number; content?: string }) =>
    api.put(`/reviews/${reviewId}`, data),
  deleteReview: (reviewId: string) => api.delete(`/reviews/${reviewId}`),
  likeReview: (reviewId: string) => api.post(`/reviews/${reviewId}/like`),
  getUserReviews: () => api.get('/reviews/user/reviews'),

  // Comments
  getComments: (reviewId: string) => api.get(`/reviews/${reviewId}/comments`),
  addComment: (reviewId: string, content: string) =>
    api.post(`/reviews/${reviewId}/comments`, { content }),
  deleteComment: (commentId: string) => api.delete(`/reviews/comments/${commentId}`),
  likeComment: (commentId: string) => api.post(`/reviews/comments/${commentId}/like`),
};

// ─────────────────────────────────────────────
// USER / SOCIAL
// ─────────────────────────────────────────────
export const userAPI = {
  getFavorites: () => api.get('/movies/user/favorites'),
  getWatchlist: () => api.get('/movies/user/watchlist'),
  getWatched: () => api.get('/movies/user/watched'),
  getNotebook: () => api.get('/movies/user/notebook'),
  getReviews: () => api.get('/reviews/user/reviews'),

  // Public profile
  getPublicProfile: (username: string) => api.get(`/users/${username}`),
  getPublicFavorites: (username: string) => api.get(`/users/${username}/favorites`),
  getPublicReviews: (username: string) => api.get(`/users/${username}/reviews`),
  getPublicWatched: (username: string) => api.get(`/users/${username}/watched`),
  getPublicNotebook: (username: string) => api.get(`/users/${username}/notebook`),

  // Follow system
  followUser: (userId: string) => api.post(`/users/${userId}/follow`),
  unfollowUser: (userId: string) => api.delete(`/users/${userId}/follow`),
  getFollowers: (userId: string) => api.get(`/users/${userId}/followers`),
  getFollowing: (userId: string) => api.get(`/users/${userId}/following`),
  isFollowing: (userId: string) => api.get(`/users/${userId}/is-following`),
};


// ─────────────────────────────────────────────
// QUIZ
// ─────────────────────────────────────────────
export const quizAPI = {
  submitQuiz: (answers: Record<string, string>) => api.post('/quiz/submit', { answers }),
  getQuizHistory: () => api.get('/quiz/history'),
};

// Notifications
// Notifications
export const getNotifications = () =>
  api.get('/notifications');

export const getUnreadCount = () =>
  api.get('/notifications/unread-count');

export const markNotificationRead = (id: number) =>
  api.patch(`/notifications/${id}/read`);

export const markAllNotificationsRead = () =>
  api.patch('/notifications/read-all');

export default api;